<template lang="pug">
.file-uploader()
    aiq-upload.file-uploader(
        v-model="imageUrl"
        :http-request="uploadImage"
        :action="url"
        :accept="acceptedFileType"
        :headers="headers"
        :disabled="disable"
        drag
        :show-file-list="false"
        :multiple="multiple"
    )
        .img-uploader-icon
          fa-icon(:icon="['fa-solid', 'fa-upload']")
        .el-upload__text
            | Drag and drop files here or
            em  click to upload
        .el-upload-tips {{ upload_tips }}
.file-list(v-if="files.length > 0")
    .file-container(v-for="(item, index)  in files" :key="index")
        .files
            a.aiq-link-preview(@click.prevent="onHandleView(item)")
                font-awesome-icon(:icon="getIconType(item)[0]"
                                :class="getIconType(item)[1]")
                span.text-info {{ getFileName(item) }}
            span.size-info ({{ getFileSize(item) }})
            fa-icon(:icon="['fas', 'xmark']" class="trash-icon" @click="onHandleDelete(item)")
        .progress-bar-container(v-if="multiple")
            aiq-progress.progress-bar(:percentage="item.progress.uploadPercent"
                :stroke-width="5"
                :format="() => ''"
                :status="item.progress.uploadStatus")
            .progress-status
            span {{item.progress.progressBarStatus}} | {{item.progress.uploadPercent}}%
        .progress-bar-container(v-else)
            aiq-progress.progress-bar(:percentage="getProgress.uploadPercent"
                :stroke-width="5"
                :format="() => ''"
                :status="getProgress.uploadStatus")
            .progress-status
            span {{getProgress.progressBarStatus}} | {{getProgress.uploadPercent}}%
</template>
<script>
import { hosts } from '@/config/api.routes';
import { IMAGE_ACL_TYPES } from '@/constants';
import {iconClasses} from './util';
import get from 'lodash/get';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';
import path from 'path-browserify';
const WAIT_TIME = 700;
const COMPLETE_UPLOAD = 100;
const ONE_KB = 1024;
const DECIMAL_PLACES = 2;


export default {
  name: 'FileUploader',
  emits: ['getRecordName', 'getFileList'],
  mixins: [GetGlobalSource],
  data() {
    return {
      imageUrl: '',
      url: `${hosts.management}files/upload`,
      headers: {
        Authorization: this.$store.getters['agent/authToken'],
      },
      files: [],
      itemsToUpload: [],
      timeoutId: null,
      uploadProgress: {},
    };
  },
  props: {
    acceptedFileType: {
      type: String,
      default: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv',
    },
    supported_files: {
      type: Array,
      default: () => ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/csv'],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    upload_tips: {
      type: String,
      default: '(DOCX, PDF, PNG - MAX 25MB)',
    },
    acl: {
      type: String,
      default: IMAGE_ACL_TYPES.PUBLIC,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    fileList: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    getProgress() {
      return {
        uploadPercent: get(this.uploadProgress, 'uploadPercent', COMPLETE_UPLOAD),
        uploadStatus: get(this.uploadProgress, 'uploadStatus', 'success'),
        progressBarStatus: get(this.uploadProgress, 'progressBarStatus', 'complete'),
      };
    },
  },
  watch: {
    fileList: {
      handler() {
        this.files = this.fileList.map(file => ({
          ...file,
          progress: {
            uploadPercent: COMPLETE_UPLOAD,
            uploadStatus: 'success',
            progressBarStatus: 'complete',
          },
        }));
      },
      deep: true,
    },
  },
  mounted() {
    this.files = this.fileList.map(file => ({
      ...file,
      progress: {
        uploadPercent: COMPLETE_UPLOAD,
        uploadStatus: 'success',
        progressBarStatus: 'complete',
      },
    }));
  },

  /**
   * Vue lifecycle hook
   * Add webkitdirectory attribute to the input element to enable folder upload
   * whenever the component is updated and recordKind is folder.
   */
  updated() {
    if (this.multiple) {
      const inputElement = document.querySelector('.el-upload__input');
      if (!inputElement) return;
      if (!inputElement.hasAttribute('webkitdirectory')) {
        inputElement.setAttribute('webkitdirectory', true);
        inputElement.setAttribute('multiple', true);
        inputElement.setAttribute('directory', true);
      }
    }
  },
  methods: {

    getIconType(item) {
      const object_type = get(item, 'mime', '');
      return iconClasses(object_type);
    },
    getFileName(item) {
      return get(item, 'name', '');
    },
    getFileSize(item) {
      const size = get(item, 'size', 0);
      if (size > ONE_KB * ONE_KB) {
        return `${(size / (ONE_KB * ONE_KB)).toFixed(DECIMAL_PLACES)} MB`;
      }
      return `${(size / ONE_KB).toFixed(DECIMAL_PLACES)} KB`;
    },
    async uploadImage(data) {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('acl', this.acl);

      if (!this.multiple) {
        this.$emit('getRecordName', data.file.name);
      } else {
        this.itemsToUpload = [data.file, ...this.itemsToUpload];
      }

      try {
        if (!this.multiple) {
          this.uploadProgress = {
            uploadPercent: 0,
            uploadStatus: 'in progress',
            progressBarStatus: 'in progress',
          };
          this.files = [{
            name: data.file.name,
            mime: data.file.type,
            size: data.file.size,
          }];
          const response = await this.$store.dispatch('files/upload', {
            formData,
            url: this.url,
            onUploadProgress: this.onUploadProgress,
          });

          //update the last item
          this.files[this.files.length - 1] = response.data;
          this.$emit('getFileList', [response.data]);

        } else {
          await this.processMultipleFiles();
        }

      } catch (error) {
        this.uploadProgress = {
          uploadPercent: COMPLETE_UPLOAD,
          uploadStatus: 'exception',
          progressBarStatus: 'failure',
        };
        this.$aiq.notify.error('Unable to upload file');
      }
    },
    async processMultipleFiles() {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        if (this.itemsToUpload.length > 0) {
          this.uploadFolderList();
        }
      }, WAIT_TIME);
    },
    folderUploadProgress(progressEvent) {
      const percent = Math.round(
        (progressEvent.loaded * COMPLETE_UPLOAD) / progressEvent.total,
      );
      if (percent !== COMPLETE_UPLOAD) {
        this.files[0].progress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'in progress',
        };
      } else {
        this.files[0].progress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },
    async uploadFolderList() {
      if (this.itemsToUpload.length < 1) {
        this.$emit('getRecordName', '');
        return;
      }
      const unsupported_files = [];
      const item = this.itemsToUpload[this.itemsToUpload.length - 1];
      this.$emit('getRecordName', path.dirname(item.webkitRelativePath));
      for (const data of this.itemsToUpload) {
        try {
          const formData = new FormData();
          formData.append('file', data);
          formData.append('acl', this.acl);
          if (!this.supported_files.includes(data.type)) {
            unsupported_files.push(data.name);
            continue;
          }
          this.files.push({
            name: data.name,
            mime: data.type,
            size: data.size,
            webkitRelativePath: data.webkitRelativePath,
            progress: {
              uploadPercent: 0,
              uploadStatus: 'success',
              progressBarStatus: 'in progress',
            },
          });
          const response = await this.$store.dispatch('files/upload', {
            formData,
            url: this.url,
            onUploadProgress: this.onUploadProgress,
          });
          //update the last item
          this.files[this.files.length - 1] = {
            ...response.data,
            webkitRelativePath: data.webkitRelativePath,
          };

          this.files[this.files.length - 1] = { ...this.files[this.files.length - 1], progress: {
            uploadPercent: COMPLETE_UPLOAD,
            uploadStatus: 'success',
            progressBarStatus: 'complete',
          } };


        } catch (error) {
          this.uploadProgress = {
            uploadPercent: COMPLETE_UPLOAD,
            uploadStatus: 'exception',
            progressBarStatus: 'failure',
          };
          this.$aiq.notify.error('Unable to upload file');
        }
      }
      if (unsupported_files.length > 0) {
        this.$aiq.notify.info(`Unsupported file types: ${unsupported_files.join(', ')}`);
      }
      this.itemsToUpload = [];
      this.$emit('getFileList', this.files);
    },
    onUploadProgress(progressEvent) {
      const percent = Math.round(
        (progressEvent.loaded * COMPLETE_UPLOAD) / progressEvent.total,
      );
      if (percent !== COMPLETE_UPLOAD) {
        this.uploadProgress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'in progress',
        };
      } else {
        this.uploadProgress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },
    async onHandleDelete(item) {
      if (this.disable) {
        return;
      }
      const file_id = get(item, 'file_id');
      if (!file_id) {
        return;
      }
      try {
        await this.$store.dispatch('files/deleteFile', file_id);
        this.files = this.files.filter((file) => file.file_id !== item.file_id);
        this.$emit('getFileList', this.files);

      } catch (error) {
        this.$aiq.notify.error('Unable to delete file');
      }
    },
    async onHandleView(item) {
      const url = await this.getFileUrl(item);
      if (url) {
        window.open(url, '_blank');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import './icon.scss';

.file-list {
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 30px;
}

:deep(.el-upload-dragger) {
  border: 2px dashed #d9d9d9;
  background-color: transparent;
  height: 150px;
}

.size-info {
  color: #999999;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}
.el-upload-tips {
    color: #999999;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
}

.aiq-link-preview{
    display: flex;
    gap: 10px;
    cursor: pointer;
}
.el-upload__text {
    color: black;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
}

.trash-icon {
   margin-right: 40px;
   float: right;
   font-size: 16px;
   cursor: pointer;
}

:deep(.el-upload-dragger) {
    width: 100%;
}
</style>